export const FILTERS_PANEL_STORAGE_PREFIX = 'filters-panel::';

export type DropdownOptionId = string | number;

export interface DropdownOptionLabelStub {
  id: DropdownOptionId;
  text: string;
}

export type DropdownOptionStubsByFieldKey = Record<string, DropdownOptionLabelStub[]>;

export type StoredFiltersPanelState = {
  version: 2;
  savedAtIso: string;
  valuesByFieldKey: Record<string, any>;
  optionStubsByFieldKey?: DropdownOptionStubsByFieldKey;
};

export function getFiltersPanelStorageKey(params: {
  storageKeyStrategy: 'route' | 'tableName';
  tableName?: string;
  routerUrl?: string;
}): string | null {
  if (params.storageKeyStrategy === 'tableName') {
    const normalizedTableName = String(params.tableName ?? '').trim();

    if (normalizedTableName.length === 0) {
      return null;
    }

    return `${FILTERS_PANEL_STORAGE_PREFIX}${normalizedTableName}`;
  }

  const url = String(params.routerUrl ?? '').split('?')[0];

  if (url.length === 0) {
    return null;
  }

  const normalized = url.replace(/\//g, '_');
  return `${FILTERS_PANEL_STORAGE_PREFIX}${normalized}`;
}

export function loadStoredFiltersPanelState(storageKey: string): StoredFiltersPanelState | null {
  const normalizedKey = String(storageKey ?? '').trim();

  if (normalizedKey.length === 0) {
    return null;
  }

  const raw = localStorage.getItem(normalizedKey);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as StoredFiltersPanelState;

    if (!isValidStoredFiltersPanelState(parsed)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function persistFiltersPanelState(params: {
  storageKey: string;
  valuesByFieldKey: Record<string, any>;
  optionStubsByFieldKey?: DropdownOptionStubsByFieldKey;
}): StoredFiltersPanelState | null {
  const normalizedKey = String(params.storageKey ?? '').trim();

  if (normalizedKey.length === 0) {
    return null;
  }

  const state: StoredFiltersPanelState = {
    version: 2,
    savedAtIso: new Date().toISOString(),
    valuesByFieldKey: params.valuesByFieldKey ?? {},
    optionStubsByFieldKey: params.optionStubsByFieldKey ?? {}
  };

  localStorage.setItem(normalizedKey, JSON.stringify(state));

  return state;
}

export function clearFiltersPanelState(storageKey: string): void {
  const normalizedKey = String(storageKey ?? '').trim();

  if (normalizedKey.length === 0) {
    return;
  }

  localStorage.removeItem(normalizedKey);
}

function isValidStoredFiltersPanelState(value: any): value is StoredFiltersPanelState {
  if (!value) {
    return false;
  }

  if (value.version !== 2) {
    return false;
  }

  const valuesByFieldKey = value.valuesByFieldKey;

  if (!valuesByFieldKey || typeof valuesByFieldKey !== 'object') {
    return false;
  }

  return true;
}
